import { api, byId, el, fmt } from './dom.js';
import { renderCronHistoryChart } from './cron-history-chart.js';
import { renderMyPurchases, renderMyPurchasesLoggedOut, renderMySummary } from './my-purchases.js';
import { renderPackageComparison } from './package-comparison.js';
import { createPackageForm } from './package-form.js';
import { createPackageHistory } from './package-history.js';
import { renderPackageGroups, renderPackageRanking } from './package-ranking.js';
import { renderPackageResult, renderQuickResult } from './package-result.js';
import { mountUxFeedback } from './ux-feedback.js';

// Um bundle para as três sub-páginas; <body data-mc-page> diz qual montar:
// overview (/melhor-cron), community (/melhor-cron/comunidade) e personal (/melhor-cron/pessoal).
const PAGE = document.body.dataset.mcPage || 'overview';
const COMMUNITY_URL = '/melhor-cron/comunidade';
const PERSONAL_URL = '/melhor-cron/pessoal';

const state = {
  user: null,
  options: null,
  stats: { distribution: [], minHistorySample: 5, bandThresholds: { excellent: 75, normal: 35, low: 10 }, summary: null },
  editingId: null,
  chartPeriod: 'all',
  rankingPeriod: 'all',
  chartPoints: []
};

// Cada página de registro grava com uma visibilidade: Comunidade publica;
// Pessoal guarda um registro privado, fora do histórico e das médias.
const MODES = {
  community: {
    visibility: 'public',
    title: 'Registrar para a comunidade',
    hint: (name) => `Visível para todos como “${name}”. Entra no histórico e nas médias da comunidade.`,
    anonymousHint: 'Sem login você pode simular e consultar. Para publicar, entre com a Twitch.',
    button: 'Publicar na comunidade',
    anonymousButton: 'Entrar para publicar',
    success: 'Pacote publicado na comunidade e salvo na sua conta. Obrigado!'
  },
  personal: {
    visibility: 'private',
    title: 'Registrar pacote pessoal',
    hint: (name) => `Só você vê (${name}). Não aparece no histórico nem nas médias da comunidade.`,
    anonymousHint: 'Sem login você pode simular. Para guardar seus pacotes, entre com a Twitch.',
    button: 'Salvar no meu histórico',
    anonymousButton: 'Entrar para salvar',
    success: 'Pacote salvo no seu histórico pessoal.'
  }
};
const mode = MODES[PAGE] || null;

let form = null;
let history = null;

function message(text, tone = '') {
  const node = byId('mcMessage');
  if (!node) { if (text && tone === 'error') console.error(text); return; }
  node.textContent = text || '';
  node.dataset.tone = tone;
}

function renderDashboard() {
  const summary = state.stats.summary || {};
  byId('mcPublicCount').textContent = summary.totalRecords ? fmt.int(summary.totalRecords) : '';
  const host = byId('mcDashboard');
  if (!host) return;
  const tile = (label, value, detail, unit) => el('div', { className: 'mc-stat' }, [el('span', { text: label }), el('strong', {}, [value, unit ? el('small', { className: 'mc-unit', text: ` ${unit}` }) : null]), detail ? el('small', { text: detail }) : null]);
  if (!summary.totalRecords) {
    host.replaceChildren(el('div', { className: 'mc-empty mc-empty-cta' }, [
      el('p', { text: 'Ainda não há compras registradas. Registre a sua e seja a primeira referência da comunidade.' }),
      el('a', { className: 'primary mc-button-link', href: COMMUNITY_URL, text: 'Registrar uma compra' })
    ]));
    return;
  }
  host.replaceChildren(
    tile('Pacotes registrados', fmt.int(summary.totalRecords)),
    tile('Melhor oferta', summary.best ? fmt.cpp(summary.best.cronPerPearl) : '—', summary.best ? `${summary.best.packageName} · ${fmt.date(summary.best.purchasedAt)}` : null, summary.best ? 'Cron/Pérola' : null),
    tile('Média 90 dias', Number.isFinite(summary.average90d) ? fmt.cpp(summary.average90d) : '—', null, Number.isFinite(summary.average90d) ? 'Cron/Pérola' : null),
    tile('Registros em 30 dias', fmt.int(summary.recordsLast30d ?? 0))
  );
}

function setPeriodChips(hostId, periods, current, onSelect) {
  const labels = { '30d': '30 dias', '90d': '90 dias', '6m': '6 meses', '1y': '1 ano', year: String(new Date().getFullYear()), all: 'Todo o histórico' };
  byId(hostId).replaceChildren(...periods.map((period) => el('button', {
    type: 'button',
    className: period === current ? 'active' : '',
    'aria-pressed': String(period === current),
    text: labels[period],
    onclick: () => onSelect(period)
  })));
}

async function loadStats() {
  state.stats = await api('/api/tools/cron-calculator/stats');
  renderDashboard();
}

async function loadChart() {
  setPeriodChips('mcChartPeriods', ['30d', '90d', '6m', '1y', 'all'], state.chartPeriod, (period) => { state.chartPeriod = period; loadChart(); });
  const data = await api(`/api/tools/cron-calculator/chart?period=${state.chartPeriod}`);
  state.chartPoints = data.points;
  renderCronHistoryChart(byId('mcChart'), state.chartPoints);
}

async function loadRanking() {
  setPeriodChips('mcRankingPeriods', ['30d', '90d', 'year', 'all'], state.rankingPeriod, (period) => { state.rankingPeriod = period; loadRanking(); });
  const data = await api(`/api/tools/cron-calculator/ranking?period=${state.rankingPeriod}`);
  renderPackageRanking(byId('mcRanking'), data.items, state.stats);
}

async function loadGroups() {
  const q = byId('mcGroupSearch').value.trim();
  const data = await api(`/api/tools/cron-calculator/groups?${new URLSearchParams(q ? { q } : {})}`);
  renderPackageGroups(byId('mcGroups'), data.items, state.stats, { onSearch: (text) => { location.href = `${COMMUNITY_URL}?${new URLSearchParams({ q: text })}`; } });
}

// Sugestões de nome no formulário, a partir das ofertas já registradas.
async function loadSuggestions() {
  const data = await api('/api/tools/cron-calculator/groups?limit=50');
  form.setCommunityNames([...new Set(data.items.map((item) => item.packageName))]);
}

async function setVisibility(item, visibility) {
  const { packageName, originalPrice, discountPercent, region, purchasedAt, packageType, notes, items } = item;
  await api(`/api/tools/cron-calculator/purchases/${encodeURIComponent(item.id)}`, {
    method: 'PUT',
    body: JSON.stringify({ packageName, originalPrice, discountPercent, region, purchasedAt, packageType, notes, items, visibility })
  });
  message(visibility === 'private' ? 'Registro movido para Pessoal.' : 'Registro publicado na comunidade.', 'success');
  await refreshAll();
}

// Contador da aba Pessoal em todas as páginas; lista completa só em /pessoal.
async function loadMine() {
  const host = byId('mcMine');
  if (!state.user) {
    byId('mcPrivateCount').textContent = '';
    if (host) {
      byId('mcMineSummary').replaceChildren();
      renderMyPurchasesLoggedOut(host, () => window.siteHeader?.openAuthModal());
    }
    return;
  }
  const data = await api('/api/tools/cron-calculator/purchases/mine');
  byId('mcPrivateCount').textContent = data.items.length ? fmt.int(data.items.length) : '';
  if (!host) return;
  renderMySummary(byId('mcMineSummary'), data.items, state.stats);
  renderMyPurchases(host, data.items, {
    stats: state.stats,
    // Registro público é editado na página Comunidade; o pessoal, aqui.
    onEdit: (item) => (item.visibility === 'private' ? startEditing(item) : location.assign(`${COMMUNITY_URL}?${new URLSearchParams({ editar: item.id })}`)),
    onToggleVisibility: (item) => setVisibility(item, item.visibility === 'private' ? 'public' : 'private').catch((error) => message(error.message, 'error')),
    onDeleted: () => { message('Registro excluído.', 'success'); refreshAll(); },
    onError: (error) => message(error.message, 'error')
  });
}

function refreshAll() {
  const loaders = [loadMine()];
  if (PAGE === 'overview') loaders.push(loadChart(), loadRanking(), loadGroups());
  if (history) loaders.push(history.load());
  if (form) loaders.push(loadSuggestions());
  return loadStats().then(() => Promise.all(loaders)).catch((error) => message(error.message, 'error'));
}

function renderMode() {
  if (!mode) return;
  const name = state.user?.nick || state.user?.name || 'você';
  byId('mcCalculator').dataset.mode = mode.visibility;
  byId('mcAuthHint').textContent = state.user ? mode.hint(name) : mode.anonymousHint;
  byId('mcPublish').textContent = state.editingId ? 'Salvar alterações' : (state.user ? mode.button : mode.anonymousButton);
  byId('mcCancelEdit').hidden = !state.editingId;
  byId('mcFormTitle').textContent = state.editingId ? `Editando: ${mode.visibility === 'private' ? 'pacote pessoal' : 'registro da comunidade'}` : mode.title;
}

function updateResult() {
  const input = form.value();
  renderPackageResult(byId('mcResult'), input, state.stats);
  renderQuickResult(byId('mcQuickResult'), input, state.stats);
}

function setEditing(item) {
  state.editingId = item?.id || null;
  renderMode();
}

function startEditing(item) {
  form.load(item);
  setEditing(item);
  byId('mcCalculator').scrollIntoView({ behavior: 'smooth' });
}

// ?editar=<id> abre um registro do próprio usuário para edição nesta página.
async function openRequestedEdit() {
  const id = new URLSearchParams(location.search).get('editar');
  if (!id || !state.user) return;
  try {
    const item = await api(`/api/tools/cron-calculator/purchases/${encodeURIComponent(id)}`);
    if (item.isOwner) startEditing(item);
  } catch (error) {
    message(error.message, 'error');
  }
}

async function publish() {
  if (!state.user) return window.siteHeader?.openAuthModal();
  const input = form.value();
  // Checagem no navegador antes do servidor: foca o primeiro campo que falta.
  const missing = renderQuickResult(byId('mcQuickResult'), input, state.stats);
  if (missing.length) {
    message(`Antes de ${state.editingId ? 'salvar' : 'publicar'}, preencha: ${missing.map((item) => item.label).join(', ')}.`, 'error');
    const target = byId(missing[0].field);
    (target.matches('input, select, textarea') ? target : target.querySelector('input:placeholder-shown, input, select'))?.focus();
    return;
  }
  // Linhas de item deixadas em branco não vão para o registro.
  const { catalogPackage, ...fields } = input;
  const body = { ...fields, items: input.items.filter((item) => item.quantity > 0), visibility: mode.visibility };
  const button = byId('mcPublish');
  button.disabled = true;
  try {
    const url = state.editingId ? `/api/tools/cron-calculator/purchases/${encodeURIComponent(state.editingId)}` : '/api/tools/cron-calculator/purchases';
    await api(url, { method: state.editingId ? 'PUT' : 'POST', body: JSON.stringify(body) });
    message(state.editingId ? 'Alterações salvas.' : mode.success, 'success');
    setEditing(null);
    form.reset();
    if (new URLSearchParams(location.search).has('editar')) window.history.replaceState(null, '', location.pathname);
    await refreshAll();
  } catch (error) {
    if (error.status === 401) window.siteHeader?.openAuthModal();
    message(error.message, 'error');
  } finally {
    button.disabled = false;
  }
}

// Mesma regra do servidor: Twitch sempre; Nickname somente onde o ambiente permite.
function setUser(user) {
  state.user = user?.id && (user.authMethod === 'twitch' || (state.options.auth.nicknameEnabled && user.authMethod === 'nickname')) ? user : null;
  if (!state.user) state.editingId = null;
  renderMode();
}

function startCalculator(options) {
  form = createPackageForm(byId('mcCalculator'), options, { onChange: () => form && updateResult() });
  byId('mcPublish').addEventListener('click', publish);
  const clear = () => { setEditing(null); form.reset(); message(''); };
  byId('mcReset').addEventListener('click', clear);
  byId('mcCancelEdit').addEventListener('click', clear);
  updateResult();
}

async function start() {
  const [options, user] = await Promise.all([api('/api/tools/cron-calculator/options'), window.siteHeader?.ready.catch(() => null)]);
  state.options = options;
  setUser(user);
  if (mode) startCalculator(options);
  if (PAGE === 'community') {
    history = createPackageHistory(byId('mcHistorySection'), options, {
      getStats: () => state.stats,
      onCompare: (items) => renderPackageComparison(byId('mcComparison'), items),
      onEdit: startEditing
    });
    const q = new URLSearchParams(location.search).get('q');
    if (q) byId('mcHistoryFilters').elements.q.value = q;
  }
  if (PAGE === 'overview') {
    let groupTimer;
    byId('mcGroupSearch').addEventListener('input', () => { clearTimeout(groupTimer); groupTimer = setTimeout(loadGroups, 350); });
    let resizeTimer;
    window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => renderCronHistoryChart(byId('mcChart'), state.chartPoints), 150); });
  }
  mountUxFeedback(byId('mcUxFeedback'), PAGE);
  window.addEventListener('authchange', (event) => { setUser(event.detail); refreshAll(); openRequestedEdit(); });
  await refreshAll();
  if (form) updateResult();
  await openRequestedEdit();
}

start().catch((error) => message(error.message, 'error'));
